/**
 * 프로젝트(Project) 프론트엔드 타입 정의
 * V140: 프로젝트 관리 MVP
 *
 * 백엔드 타입과 동기화되어야 합니다.
 * @see functions/src/types/project.types.ts
 */

import { Timestamp } from 'firebase/firestore';

// ============================================
// 프로젝트 상태 타입
// ============================================

/**
 * 프로젝트 상태
 */
export type ProjectStatus = 'active' | 'completed' | 'onHold' | 'cancelled';

/**
 * 프로젝트 상태 한글 라벨
 */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active: '진행중',
  completed: '완료',
  onHold: '중단',
  cancelled: '취소',
};

/**
 * 프로젝트 상태 색상 (MantineUI Badge color)
 */
export const PROJECT_STATUS_COLORS: Record<ProjectStatus, string> = {
  active: 'green',
  completed: 'blue',
  onHold: 'yellow',
  cancelled: 'gray',
};

// ============================================
// V91: 프로젝트 태그 마스터 (전사 공용)
// ============================================

/**
 * 태그 종류
 * - workType: 업무형태 (QA, 로컬, 개발 등)
 * - projectDomain: 프로젝트 분야 (게임, 블록체인, 웹서비스 등)
 */
export type ProjectTagType = 'workType' | 'projectDomain';

/**
 * 태그 상태
 */
export type ProjectTagStatus = 'active' | 'archived';

/**
 * 프로젝트 태그 마스터 문서
 * V91: 전사 공용 (companyCode 제거)
 */
export interface ProjectTagMaster {
  id: string;
  type: ProjectTagType;
  label: string;
  /** V91: 대소문자 무시 중복 체크용 (label.trim().toLowerCase()) */
  labelNormalized: string;
  status: ProjectTagStatus;
  sortOrder: number;
  createdAt: Timestamp | Date;
  createdBy: string;
  updatedAt: Timestamp | Date;
  updatedBy: string;
}

/**
 * 태그 종류별 한글 라벨
 */
export const PROJECT_TAG_TYPE_LABELS: Record<ProjectTagType, string> = {
  workType: '업무형태',
  projectDomain: '프로젝트 분야',
};

// ============================================
// 프로젝트 마스터 (projects)
// ============================================

/**
 * 프로젝트 마스터 문서
 */
export interface Project {
  id: string;
  name: string;
  ownerOrgUnitId: string;
  ownerOrgUnitNameSnapshot: string;
  status: ProjectStatus;
  currentRevisionId: string;
  description?: string;
  /** V90: 업무형태 태그 ID 배열 */
  workTypeTags?: string[];
  /** V90: 프로젝트 분야 태그 ID 배열 */
  projectDomainTags?: string[];
  /** V98: 고객사 마스터 ID (customers 컬렉션 참조) */
  customerId?: string;
  /**
   * V69 (2026-05-20): 과거 프로젝트 플래그.
   * - true: 실 직원 매핑 없이 actualStaffing 만 활용. 인력 현황판/중복투입/자동리비전 제외.
   */
  isHistorical?: boolean;
  /**
   * V69 (2026-05-20): 명시적 국가 코드 (KR or CN).
   * - 신규 프로젝트는 BE 가 ownerOrgUnitId → root → companyCode 추론 후 저장.
   * - listProjects 응답에는 read-side fallback 도 채워서 내려옴.
   * - 국가 필터(KR/CN/ALL)의 SSoT.
   */
  companyCode?: 'KR' | 'CN';
  createdAt: Timestamp | Date;
  createdBy: string;
  updatedAt: Timestamp | Date;
  updatedBy: string;
}

// ============================================
// 프로젝트 리비전 (projectRevisions)
// ============================================

/**
 * 고객사 정보 (리비전 내 스냅샷)
 * V99: 고객사명 단일 필드화. 프로젝트에 customerId가 있으면 마스터명 우선 표시.
 */
export interface ProjectCustomer {
  /** 고객사명 (마스터에서 스냅샷) */
  name: string;
  /**
   * V99.3: 담당자명 (리비전 수준에서 관리)
   */
  contactName?: string;
  /**
   * V99.3: 담당자 연락처 (리비전 수준에서 관리)
   */
  contactPhone?: string;
  /**
   * V99.3: 담당자 이메일 (리비전 수준에서 관리)
   */
  contactEmail?: string;
}

// ============================================
// 등급 타입 (V140.2+V140.3)
// ============================================

/**
 * 투입 인력 등급
 * - 특급: 특급 기술사
 * - 고급: 고급 기술사
 * - 중급: 중급 기술사
 * - 초급: 초급 기술사
 * - 등급무관: 등급 미지정
 */
export type StaffGrade = '특급' | '고급' | '중급' | '초급' | '등급무관';

/**
 * 등급 목록 상수
 */
export const STAFF_GRADES: StaffGrade[] = ['특급', '고급', '중급', '초급', '등급무관'];

/**
 * 등급별 계약 인원 정보
 * V140.3: 등급별 계약 리소스 인원 수
 * @deprecated V75: GradeBasedFTE로 대체. 기존 데이터 호환용.
 */
export interface ContractedResource {
  grade: StaffGrade;
  headcount: number;
}

// ============================================
// V75: FTE 기반 등급별 인원 타입
// ============================================

/**
 * V75: 등급별 FTE 숫자 (0.5 단위)
 *
 * FTE (Full-Time Equivalent):
 * - 1.0 = 전일 투입 (월 20MD 기준)
 * - 0.5 = 반일 투입 (월 10MD 기준)
 */
export interface GradeBasedFTE {
  special: number;  // 특급
  senior: number;   // 고급
  mid: number;      // 중급
  junior: number;   // 초급
  any: number;      // 등급무관
}

/**
 * V75: 투입 인원 배정 정보
 *
 * V69 (2026-05-19): 외부/기타 인력 지원 추가.
 *   - 내부 직원: userId 필수, isExternal=false 또는 undefined
 *   - 외부 인력: isExternal=true + externalName + externalOrgName 필수, userId 빈 문자열 또는 미사용
 *   - users 컬렉션에 별도 레코드 생성 안 함
 */
export interface StaffAssignment {
  /** V69: 외부 인력 식별 플래그 */
  isExternal?: boolean;
  /** V69: 외부 인력일 경우 optional (빈 문자열 가능) */
  userId?: string;
  nameSnapshot?: string;
  /** V69: 외부 인력 이름 (isExternal=true 시 필수) */
  externalName?: string;
  /** V69: 외부 인력 소속명 (isExternal=true 시 필수) */
  externalOrgName?: string;
  grade: StaffGrade;
  fte: number;      // 0.5 단위
  role?: string;    // 예: 'PM', 'PL', 'AA', 'DA', 'TA'
  memo?: string;
}

/**
 * V75: GradeBasedFTE 초기값 생성 헬퍼
 */
export function createEmptyGradeBasedFTE(): GradeBasedFTE {
  return { special: 0, senior: 0, mid: 0, junior: 0, any: 0 };
}

/**
 * V75: StaffGrade → GradeBasedFTE 키 매핑
 */
export const GRADE_TO_FTE_KEY: Record<StaffGrade, keyof GradeBasedFTE> = {
  '특급': 'special',
  '고급': 'senior',
  '중급': 'mid',
  '초급': 'junior',
  '등급무관': 'any',
};

/**
 * V75: GradeBasedFTE 키 → StaffGrade 매핑
 */
export const FTE_KEY_TO_GRADE: Record<keyof GradeBasedFTE, StaffGrade> = {
  special: '특급',
  senior: '고급',
  mid: '중급',
  junior: '초급',
  any: '등급무관',
};

/**
 * V75: FTE 키 목록 (순서대로)
 */
export const FTE_KEYS: (keyof GradeBasedFTE)[] = ['special', 'senior', 'mid', 'junior', 'any'];

/**
 * 계약 정보
 * V140.3: 등급별 인원/단가 구조로 변경
 * V70: flatPrice, discount 필드 추가
 * V75: contractStaffing 추가 (0.5 FTE 단위)
 */
export interface ProjectContract {
  /** 계약 시작일 (YYYY-MM-DD) */
  startDate: string;
  /** 계약 종료일 (YYYY-MM-DD) */
  endDate: string;
  /**
   * 등급별 계약 인원 수
   * @deprecated V75: contractStaffing으로 대체. 기존 데이터 호환용.
   */
  contractedResources?: ContractedResource[];
  /**
   * V75: 등급별 계약 인원 FTE (0.5 단위)
   */
  contractStaffing?: GradeBasedFTE;
  /** 등급별 단가 (원/월) */
  unitPricesByGrade: Partial<Record<StaffGrade, number>>;
  /** 계약 메모 (선택) */
  notes?: string;
  /**
   * V70: 통단가 (원/월)
   * 등급 무관 일괄 금액. 총단가에 가산됨.
   */
  flatPrice?: number;
  /**
   * V70: 할인가 (원/월)
   * 차감 금액. 총단가에서 차감됨.
   */
  discount?: number;
}

/**
 * 프로젝트 리드 정보
 * V140.2: 리드 1명 + 등급
 */
export interface ProjectLead {
  /**
   * 직원 ID (users 컬렉션 ID)
   */
  userId: string;

  /**
   * 직원명 스냅샷 (users.nameKo)
   */
  nameSnapshot: string;

  /**
   * 등급
   */
  grade: StaffGrade;
}

/**
 * 투입 인력 정보
 * V140.2: 실투입 인원 N명 + 각 등급
 */
export interface StaffMember {
  /**
   * 직원 ID (users 컬렉션 ID)
   */
  userId: string;

  /**
   * 직원명 스냅샷 (users.nameKo)
   */
  nameSnapshot: string;

  /**
   * 등급
   */
  grade: StaffGrade;
}

/**
 * 인력 투입 정보
 * V140.2: 리드 1명 + 멤버 N명 구조
 */
export interface ProjectStaffing {
  /**
   * 프로젝트 리드 (PM/PL)
   * 선택 사항
   */
  lead?: ProjectLead;

  /**
   * 실투입 인원 목록
   */
  members: StaffMember[];
}

/**
 * 프로젝트 리비전 문서
 * V75: actualStaffing, staffAssignments 추가
 * V82: renewedAt 추가
 */
export interface ProjectRevision {
  id: string;
  projectId: string;
  revisionNo: number;
  effectiveFrom: string; // YYYY-MM-DD
  customer: ProjectCustomer;
  contract: ProjectContract;
  /**
   * 인력 투입 정보
   * @deprecated V75: actualStaffing + staffAssignments로 대체. 기존 데이터 호환용.
   */
  staffing?: ProjectStaffing;
  /**
   * V75: 실투입 인원 FTE (0.5 단위)
   */
  actualStaffing?: GradeBasedFTE;
  /**
   * V75: 투입 인원 배정 목록
   */
  staffAssignments?: StaffAssignment[];
  /**
   * V82: 계약일 (YYYY-MM-DD)
   *
   * 계약이 실행된 날짜 (계약서 작성/서명일).
   * effectiveFrom(적용일)과 다름:
   * - renewedAt(계약일): 계약서 작성/서명일 (과거 날짜 가능)
   * - effectiveFrom(적용일): 계약 효력이 발휘되는 날짜
   *
   * V85: 프로젝트 생성 및 계약 갱신 시 입력.
   */
  renewedAt?: string;
  createdAt: Timestamp | Date;
  createdBy: string;
}

/**
 * 프로젝트 + 현재 리비전 조인된 데이터
 */
export interface ProjectWithRevision extends Project {
  currentRevision?: ProjectRevision;
}

// ============================================
// API 요청/응답 타입
// ============================================

/**
 * listProjects 요청
 */
export interface ListProjectsRequest {
  scopeOrgUnitIds: string[];
  status?: ProjectStatus;
  search?: string;
}

/**
 * listProjects 응답
 */
export interface ListProjectsResponse {
  success: boolean;
  projects: ProjectWithRevision[];
  debugMeta?: {
    scopeCount: number;
    totalProjects: number;
  };
}

/**
 * createProject 요청
 * V91: 태그 필드 추가
 */
export interface CreateProjectRequest {
  name: string;
  ownerOrgUnitId: string;
  description?: string;
  /** V98: 고객사 마스터 ID (선택 시) */
  customerId?: string;
  /** V91: 업무형태 태그 ID 배열 */
  workTypeTags?: string[];
  /** V91: 프로젝트 분야 태그 ID 배열 */
  projectDomainTags?: string[];
  /**
   * V69 (2026-05-20): 과거 프로젝트 플래그.
   * - true: staffAssignments=[], actualStaffing 만 사용. 인력 현황판/중복투입/자동리비전/계약갱신 제외.
   */
  isHistorical?: boolean;
  initialRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>;
}

/**
 * createProject 응답
 */
export interface CreateProjectResponse {
  success: boolean;
  projectId?: string;
  revisionId?: string;
  error?: string;
}

/**
 * updateProjectMaster 요청
 * V91: 태그 필드 추가
 */
export interface UpdateProjectMasterRequest {
  projectId: string;
  patch: Partial<Pick<Project, 'name' | 'status' | 'description' | 'workTypeTags' | 'projectDomainTags'>>;
}

/**
 * updateProjectMaster 응답
 */
export interface UpdateProjectMasterResponse {
  success: boolean;
  error?: string;
}

/**
 * editProjectRevision 요청
 */
export interface EditProjectRevisionRequest {
  projectId: string;
  revisionId: string;
  patch: Partial<Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>>;
}

/**
 * editProjectRevision 응답
 */
export interface EditProjectRevisionResponse {
  success: boolean;
  error?: string;
}

/**
 * renewProject 요청
 * V100: status 필드 추가 (프로젝트 상태 동시 변경)
 */
export interface RenewProjectRequest {
  projectId: string;
  newRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>;
  /** V100: 프로젝트 상태 변경 (선택) - 리비전 생성과 함께 마스터 status 업데이트 */
  status?: ProjectStatus;
}

/**
 * renewProject 응답
 */
export interface RenewProjectResponse {
  success: boolean;
  newRevisionId?: string;
  newRevisionNo?: number;
  error?: string;
}

/**
 * deleteProjects 요청
 */
export interface DeleteProjectsRequest {
  projectIds: string[];
}

/**
 * deleteProjects 응답
 */
export interface DeleteProjectsResponse {
  success: boolean;
  deletedCount?: number;
  error?: string;
}

/**
 * getProjectHistory 요청
 */
export interface GetProjectHistoryRequest {
  projectId: string;
}

/**
 * getProjectHistory 응답
 */
export interface GetProjectHistoryResponse {
  success: boolean;
  project?: Project;
  revisions?: ProjectRevision[];
  error?: string;
}

// ============================================
// V82→V83: 프로젝트 갱신 예약
// ============================================

/**
 * V82→V83: 프로젝트 갱신 예약 상태
 *
 * V83 상태 머신:
 * - pending: 대기 중
 * - applying: 적용 중 (스케줄러 선점)
 * - applied: 적용 완료
 * - failed: 적용 실패 (재시도 대상)
 * - cancelled: 취소됨
 */
export type ProjectRenewalReservationStatus = 'pending' | 'applying' | 'applied' | 'failed' | 'cancelled';

/**
 * V82→V83: 프로젝트 갱신 예약 문서
 */
export interface ProjectRenewalReservation {
  id: string;
  projectId: string;
  scheduledAt: string;  // YYYY-MM-DD
  status: ProjectRenewalReservationStatus;
  payloadSnapshot: {
    effectiveFrom: string;
    renewedAt: string;
    customer: ProjectCustomer;
    contract: ProjectContract;
    actualStaffing?: GradeBasedFTE;
    staffAssignments?: StaffAssignment[];
    /** V100: 프로젝트 상태 변경 (선택) */
    projectStatus?: ProjectStatus;
    /** V275: 마스터 정보 변경 (선택) */
    masterSnapshot?: {
      name?: string;
      description?: string;
      workTypeTags?: string[];
      projectDomainTags?: string[];
    };
  };
  createdBy: string;
  createdAt: Timestamp | Date;
  // V83: applying 상태 필드
  applyingAt?: Timestamp | Date;
  applyingBy?: string;
  // applied 상태 필드
  appliedAt?: Timestamp | Date;
  appliedRevisionId?: string;
  // V83: failed 상태 필드
  failedAt?: Timestamp | Date;
  failedReason?: string;
  // cancelled 상태 필드
  cancelledAt?: Timestamp | Date;
  cancelledBy?: string;
}

/**
 * V82: scheduleProjectRenewal 요청
 * V100: status 필드 추가 (예약 적용 시 프로젝트 상태 동시 변경)
 */
export interface ScheduleProjectRenewalRequest {
  projectId: string;
  scheduledAt: string;
  newRevision: Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'>;
  /** V100: 프로젝트 상태 변경 (선택) - 스케줄러 적용 시 마스터 status 업데이트 */
  status?: ProjectStatus;
  /** V275: 마스터 정보 변경 (선택) - 예약 적용 시 name/description/tags 업데이트 */
  masterUpdate?: {
    name?: string;
    description?: string;
    workTypeTags?: string[];
    projectDomainTags?: string[];
  };
}

/**
 * V82: scheduleProjectRenewal 응답
 */
export interface ScheduleProjectRenewalResponse {
  success: boolean;
  reservationId?: string;
  error?: string;
}

/**
 * V82: cancelProjectRenewalReservation 요청
 */
export interface CancelProjectRenewalReservationRequest {
  reservationId: string;
}

/**
 * V82: cancelProjectRenewalReservation 응답
 */
export interface CancelProjectRenewalReservationResponse {
  success: boolean;
  error?: string;
}

/**
 * V82: getProjectRenewalReservations 요청
 */
export interface GetProjectRenewalReservationsRequest {
  projectId: string;
  status?: ProjectRenewalReservationStatus;
}

/**
 * V82: getProjectRenewalReservations 응답
 */
export interface GetProjectRenewalReservationsResponse {
  success: boolean;
  reservations?: ProjectRenewalReservation[];
  error?: string;
}

/**
 * V82: 편집 모드 타입
 */
export type ProjectEditMode = 'overwrite' | 'renew' | 'scheduleRenew';

/**
 * V82: 편집 모드 라벨
 */
export const PROJECT_EDIT_MODE_LABELS: Record<ProjectEditMode, string> = {
  overwrite: '현재 계약 편집',
  renew: '계약 갱신',
  scheduleRenew: '계약 갱신 예약',
};

/**
 * V82: 편집 모드 설명
 */
export const PROJECT_EDIT_MODE_DESCRIPTIONS: Record<ProjectEditMode, string> = {
  overwrite: '현재 계약을 편집합니다. 신규 버전을 생성하지 않습니다.',
  renew: '계약을 갱신합니다. 신규 버전이 생성됩니다.',
  scheduleRenew: '계약 갱신 예약을 합니다. 예약일에 계약이 갱신됩니다.',
};

// ============================================
// 폼 초기값 헬퍼
// ============================================

/**
 * 빈 고객사 정보
 */
export const EMPTY_CUSTOMER: ProjectCustomer = {
  name: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
};

/**
 * 빈 계약 정보
 * V140.3: 새 스키마 적용
 * V70: flatPrice, discount 추가
 * V75: contractStaffing 추가
 * V77: contractedResources 제거 (SSoT = contractStaffing)
 */
export const EMPTY_CONTRACT: ProjectContract = {
  startDate: '',
  endDate: '',
  // V77: contractedResources 제거 - SSoT는 contractStaffing
  contractStaffing: createEmptyGradeBasedFTE(),
  unitPricesByGrade: {},
  notes: '',
  flatPrice: 0,
  discount: 0,
};

/**
 * 기본 등급별 계약 인원 초기화 (모든 등급 0명)
 */
export function createDefaultContractedResources(): ContractedResource[] {
  return STAFF_GRADES.map((grade) => ({ grade, headcount: 0 }));
}

/**
 * 등급별 소계 계산 함수
 * V70: calculateTotalContractPrice에서 분리
 * V75: contractStaffing 우선, 없으면 contractedResources fallback
 */
export function calculateGradeSubtotal(contract: ProjectContract): number {
  let total = 0;

  // V75: contractStaffing 우선
  if (contract.contractStaffing) {
    for (const grade of STAFF_GRADES) {
      const key = GRADE_TO_FTE_KEY[grade];
      const fte = contract.contractStaffing[key] || 0;
      const price = contract.unitPricesByGrade[grade] || 0;
      total += fte * price;
    }
    return total;
  }

  // 기존 방식 fallback
  if (contract.contractedResources) {
    for (const res of contract.contractedResources) {
      const price = contract.unitPricesByGrade[res.grade] || 0;
      total += res.headcount * price;
    }
  }
  return total;
}

/**
 * 총단가 계산 함수
 * V140.3: 등급별 인원 * 등급별 단가 합계
 * V70: + 통단가 - 할인가
 *
 * 계산식: SUM(등급별_인원 × 등급별_단가) + 통단가 - 할인가
 */
export function calculateTotalContractPrice(contract: ProjectContract): number {
  // 1. 등급별 소계
  const gradeSubtotal = calculateGradeSubtotal(contract);

  // 2. V70 신규 필드 (없으면 0 처리 - 하위호환)
  const flatPrice = contract.flatPrice ?? 0;
  const discount = contract.discount ?? 0;

  // 3. 최종 계산
  return gradeSubtotal + flatPrice - discount;
}

/**
 * 등급별 계약 인원 합계 (FTE)
 * V75: contractStaffing 우선, 없으면 contractedResources fallback
 */
export function getTotalContractedHeadcount(contract: ProjectContract): number {
  // V75: contractStaffing 우선
  if (contract.contractStaffing) {
    return getTotalFTE(contract.contractStaffing);
  }

  // 기존 방식 fallback
  return (contract.contractedResources || []).reduce((sum, res) => sum + res.headcount, 0);
}

/**
 * staffing에서 등급별 실투입 인원 집계
 * @deprecated V75: getActualStaffingByGrade 사용 권장
 */
export function aggregateStaffingByGrade(staffing: ProjectStaffing | undefined): Record<StaffGrade, number> {
  const result: Record<StaffGrade, number> = {
    '특급': 0,
    '고급': 0,
    '중급': 0,
    '초급': 0,
    '등급무관': 0,
  };

  if (!staffing) return result;

  // 리드 포함
  if (staffing.lead) {
    result[staffing.lead.grade]++;
  }

  // 멤버 집계
  for (const member of staffing.members || []) {
    result[member.grade]++;
  }

  return result;
}

/**
 * V75: actualStaffing 또는 staffing에서 등급별 FTE 집계
 */
export function getActualStaffingByGrade(revision: ProjectRevision): Record<StaffGrade, number> {
  // V75: actualStaffing 우선
  if (revision.actualStaffing) {
    const result: Record<StaffGrade, number> = {
      '특급': 0,
      '고급': 0,
      '중급': 0,
      '초급': 0,
      '등급무관': 0,
    };
    for (const grade of STAFF_GRADES) {
      const key = GRADE_TO_FTE_KEY[grade];
      result[grade] = revision.actualStaffing[key] || 0;
    }
    return result;
  }

  // 기존 staffing fallback
  return aggregateStaffingByGrade(revision.staffing);
}

/**
 * 실투입 총 인원 (리드 + 멤버)
 * @deprecated V75: getTotalActualStaffing 사용 권장
 */
export function getTotalStaffingHeadcount(staffing: ProjectStaffing | undefined): number {
  if (!staffing) return 0;
  return (staffing.lead ? 1 : 0) + (staffing.members?.length || 0);
}

/**
 * V75: actualStaffing 또는 staffing에서 총 FTE 계산
 */
export function getTotalActualStaffing(revision: ProjectRevision): number {
  // V75: actualStaffing 우선
  if (revision.actualStaffing) {
    return getTotalFTE(revision.actualStaffing);
  }

  // 기존 staffing fallback
  return getTotalStaffingHeadcount(revision.staffing);
}

// ============================================
// V75: FTE 관련 헬퍼 함수
// ============================================

/**
 * V75: GradeBasedFTE 합계 계산
 */
export function getTotalFTE(fte: GradeBasedFTE | undefined): number {
  if (!fte) return 0;
  return fte.special + fte.senior + fte.mid + fte.junior + fte.any;
}

/**
 * V75: FTE 값 0.5 단위 검증
 */
export function isValidFTEValue(value: number): boolean {
  return value >= 0 && value % 0.5 === 0;
}

/**
 * V75: GradeBasedFTE 모든 값 유효성 검증
 */
export function isValidGradeBasedFTE(fte: GradeBasedFTE | undefined): boolean {
  if (!fte) return true;
  return FTE_KEYS.every(key => isValidFTEValue(fte[key]));
}

/**
 * V75: 기존 ContractedResource[] → GradeBasedFTE 변환
 */
export function convertContractedResourcesToFTE(
  resources: ContractedResource[] | undefined
): GradeBasedFTE {
  const fte = createEmptyGradeBasedFTE();
  if (!resources) return fte;

  for (const res of resources) {
    const key = GRADE_TO_FTE_KEY[res.grade];
    if (key) {
      fte[key] = res.headcount;
    }
  }
  return fte;
}

/**
 * V75: GradeBasedFTE → ContractedResource[] 변환 (하위호환용)
 */
export function convertFTEToContractedResources(
  fte: GradeBasedFTE | undefined
): ContractedResource[] {
  if (!fte) {
    return STAFF_GRADES.map(grade => ({ grade, headcount: 0 }));
  }
  return STAFF_GRADES.map(grade => ({
    grade,
    headcount: fte[GRADE_TO_FTE_KEY[grade]] || 0,
  }));
}

/**
 * V75: staffAssignments에서 등급별 FTE 합계 계산
 */
export function aggregateAssignmentsByGrade(
  assignments: StaffAssignment[] | undefined
): GradeBasedFTE {
  const fte = createEmptyGradeBasedFTE();
  if (!assignments) return fte;

  for (const a of assignments) {
    const key = GRADE_TO_FTE_KEY[a.grade];
    if (key) {
      fte[key] += a.fte;
    }
  }
  return fte;
}

/**
 * V75: staffAssignments 총 FTE 합계
 */
export function getTotalAssignmentsFTE(
  assignments: StaffAssignment[] | undefined
): number {
  if (!assignments) return 0;
  return assignments.reduce((sum, a) => sum + a.fte, 0);
}

/**
 * 빈 인력 정보
 * @deprecated V75: actualStaffing + staffAssignments 사용 권장
 */
export const EMPTY_STAFFING: ProjectStaffing = {
  lead: undefined,
  members: [],
};

/**
 * V75: 빈 실투입 FTE
 */
export const EMPTY_ACTUAL_STAFFING: GradeBasedFTE = createEmptyGradeBasedFTE();

/**
 * 프로젝트 생성 시 초기 리비전
 * V75: actualStaffing, staffAssignments 추가
 */
export function createInitialRevision(): Omit<ProjectRevision, 'id' | 'projectId' | 'revisionNo' | 'createdAt' | 'createdBy'> {
  const today = new Date().toISOString().slice(0, 10);
  return {
    effectiveFrom: today,
    customer: { ...EMPTY_CUSTOMER },
    contract: { ...EMPTY_CONTRACT, startDate: today, contractStaffing: createEmptyGradeBasedFTE() },
    // V75: 새 스키마
    actualStaffing: createEmptyGradeBasedFTE(),
    staffAssignments: [],
    // 기존 스키마 (하위호환)
    staffing: { ...EMPTY_STAFFING },
  };
}
